import { z } from 'zod';

/**
 * **Who is flying together, frozen for the whole expedition.**
 *
 * ## Why a composition is frozen at all
 *
 * Because "who is my ally" decides whether a shot lands, whether a blast
 * reaches, whether a saw cuts and whether a heal finds you. A relationship that
 * could change mid-fight would mean a projectile that was legal when it was
 * fired and illegal when it arrived - and the two ends of that question are
 * answered in different rooms, milliseconds apart, with no way to agree.
 *
 * So a composition is decided once, by a trusted boundary, and travels frozen
 * with every pilot's profile. Nothing in a room can add a member, remove one or
 * reorder them, and there is no field through which a browser could try.
 *
 * ## Solo is an expedition of one
 *
 * There is no "party mode" and no null case. A pilot flying alone carries an
 * expedition whose only member is themselves, which makes every rule below
 * total: `enemiesOf` is "everybody not in my composition", and for a solo pilot
 * that is everybody, exactly as it was before parties existed.
 *
 * ## What a composition may NOT do
 *
 * Change. Once an expedition is under way there is no invite, no kick and no
 * swap: death, a lost connection and a border crossing all leave the member in
 * the list. Ending the expedition - when everybody has died or extracted - is a
 * later gate's decision, and until it exists an expedition simply stays active.
 */

/**
 * **The most pilots one expedition may hold. SEVEN, the leader included.**
 *
 * The owner's decision, gate V28I.AA — it was nine, and nine was declared
 * provisional the day it was written (ADR 0077, now superseded by ADR 0088).
 *
 * ## It is written ONCE
 *
 * Everything reads it: the composition schema, the lobby's ceiling, the map's
 * ally list, the handoff contract, the entry formation and the interface. There
 * is no second constant with this meaning, and no `7` in an `if`, a schema, a
 * validation or a caption. Moving the ceiling is moving this literal.
 *
 * ## It is a PARTY rule, and not a quadrant's capacity
 *
 * Seven is how many pilots may fly as ONE composition. It says nothing about how
 * many hulls a quadrant may hold: a quadrant may contain several parties, solo
 * pilots and enemies at once, and **that** number is still unmeasured and still
 * open (`docs/OPEN_QUESTIONS.md`, item 41). Reusing this number as a room
 * ceiling would be deciding that question by accident.
 */
export const OPEN_WORLD_MAX_EXPEDITION_MEMBERS = 7;

/**
 * The composition's own format version.
 *
 * Bumped when the SHAPE of a composition changes - a field added, a meaning
 * moved. It is not the number a boundary increments when it forms a different
 * party: two different parties are two different `expeditionId`s, because a
 * composition that could be edited is exactly what this file refuses to model.
 */
export const OPEN_WORLD_EXPEDITION_VERSION = 1;

/**
 * **The frozen composition.**
 *
 * It arrives with a pilot's profile from the boundary that seats them, and is
 * re-validated on arrival for the same reason the profile is: a room that
 * trusted an input on account of where it came from would be one refactor away
 * from trusting a client.
 */
export interface OpenWorldExpedition {
  /** The composition's shape version, not a party's revision. */
  readonly v: number;
  /** This expedition's identity. Two pilots are allies only if these match. */
  readonly expeditionId: string;
  /**
   * The party this expedition was formed from, when there was one.
   *
   * `null` for a solo pilot and for any expedition a future boundary forms
   * without a standing party. **No client ever chooses or changes it** - there
   * is no request in this system that carries one.
   */
  readonly partyId: string | null;
  /** Whose expedition it is. Always one of {@link members}. */
  readonly leaderAccountId: string;
  /**
   * The canonical order, 1..7 accounts.
   *
   * The ORDER is part of the contract, not a convenience: it is what breaks a
   * tie when two allies are equally close to a heal, and a list that could be
   * sorted differently on two machines would break that tie differently.
   */
  readonly members: readonly string[];
  /** Whether this expedition is still under way. Ending one is a later gate. */
  readonly active: boolean;
}

const accountIdSchema = z.string().min(1).max(128);

export const openWorldExpeditionSchema = z
  .object({
    v: z.literal(OPEN_WORLD_EXPEDITION_VERSION),
    expeditionId: z.uuid(),
    partyId: z.uuid().nullable(),
    leaderAccountId: accountIdSchema,
    members: z.array(accountIdSchema).min(1).max(OPEN_WORLD_MAX_EXPEDITION_MEMBERS),
    active: z.boolean(),
  })
  .strict()
  .refine((value) => value.members.includes(value.leaderAccountId), {
    message: 'the leader is always a member',
  })
  .refine((value) => new Set(value.members).size === value.members.length, {
    message: 'a composition holds each account once',
  });

/**
 * **The digest: one number that says "this is the same composition".**
 *
 * ## Why a composition needs one
 *
 * Because two pilots each carry their own copy of it, frozen by the boundary
 * that seated them - and two copies can disagree. A pilot seated before a
 * boundary change and one seated after would both claim the same
 * `expeditionId` while listing different members, and "are we allies" would
 * have two answers depending on which hull was asked.
 *
 * The digest makes that impossible to miss: two hulls are allies only when
 * their expedition ids AND their digests match, so a composition that differs
 * by one member is a different composition and the two are simply not allies.
 * Refusing an alliance is the safe direction - the worst case is two friends
 * who cannot shoot through each other, rather than a stranger who cannot be
 * shot.
 *
 * ## Why FNV-1a and not a hash
 *
 * Because `@kenocyber/shared` is bundled into a browser and this must be exact,
 * platform-neutral and dependency-free. It is a 64-bit integer fold in `bigint`
 * arithmetic: identical in Node, in workerd and in Chromium, to the bit.
 *
 * It is **not** a security boundary and does not need to be one. Nothing is
 * authorised by a digest - it only detects two copies of a list disagreeing,
 * and a pilot who forged one would be forging a composition the boundary never
 * issued, which the boundary's own signature is what refuses.
 */
export function openWorldExpeditionDigest(expedition: OpenWorldExpedition): string {
  /*
   * A canonical serialisation with a separator that cannot appear in any of the
   * parts. Account ids and UUIDs never contain a newline, so no two different
   * compositions can fold to the same string before they fold to the same
   * number.
   */
  const canonical = [
    String(expedition.v),
    expedition.expeditionId,
    expedition.partyId ?? '',
    expedition.leaderAccountId,
    expedition.active ? '1' : '0',
    ...expedition.members,
  ].join('\n');

  return fnv1a64(canonical).toString(16).padStart(16, '0');
}

/** A digest as it travels: sixteen lower-case hex digits, and never anything else. */
export const openWorldExpeditionDigestSchema = z.string().regex(/^[0-9a-f]{16}$/);

/**
 * **Are these two pilots allies?**
 *
 * The whole rule, and it is two comparisons: the same expedition, and the same
 * frozen composition of it. Nothing else in this codebase may answer the
 * question - a second answer written somewhere else is how a projectile comes
 * to be legal for the collision test and illegal for the damage.
 *
 * Reflexive on purpose: a hull is its own ally. Every caller that means
 * "somebody else" says so by comparing identities, which is a thing the caller
 * already has to do and this function has no business guessing.
 */
export function openWorldAreAllies(
  left: Pick<OpenWorldExpedition, 'expeditionId'> & { readonly digest: string },
  right: Pick<OpenWorldExpedition, 'expeditionId'> & { readonly digest: string },
): boolean {
  return left.expeditionId === right.expeditionId && left.digest === right.digest;
}

/**
 * **The team a hull belongs to, as one opaque string.**
 *
 * It exists because `damage/reflection.ts` asks "are these two on different
 * teams" and knows nothing about expeditions - and must not have to. Folding
 * the identity and the digest into one value means the shared rule gets the
 * right answer for allies without learning what an expedition is.
 *
 * The same two comparisons {@link openWorldAreAllies} makes, in a form a leaf
 * can compare with `!==`.
 */
export function openWorldExpeditionTeam(expeditionId: string, digest: string): string {
  return `${expeditionId}:${digest}`;
}

/**
 * Where this account stands in the canonical order, or `-1`.
 *
 * The ONLY tie-break a rule may use. Never a position in an array a room
 * happened to build, never an entity id, and never the order in which pilots
 * arrived: those are incidental, and two machines can build them differently.
 */
export function openWorldMemberOrder(expedition: OpenWorldExpedition, accountId: string): number {
  return expedition.members.indexOf(accountId);
}

/**
 * A solo pilot's expedition. One member, and it is them.
 *
 * Exists so that "everybody has an expedition" is true without a boundary
 * having to special-case a pilot who is alone - and so that nothing downstream
 * needs a null branch.
 */
export function openWorldSoloExpedition(
  expeditionId: string,
  accountId: string,
): OpenWorldExpedition {
  return Object.freeze({
    v: OPEN_WORLD_EXPEDITION_VERSION,
    expeditionId,
    partyId: null,
    leaderAccountId: accountId,
    members: Object.freeze([accountId]),
    active: true,
  });
}

/**
 * **A solo pilot's expedition id, derived from their account and nothing else.**
 *
 * ## Why derived and not minted
 *
 * Because a record written before expeditions existed has to be read back as
 * something, and "something" has to be the SAME thing every time. A minted id
 * would make a room's identity depend on when it happened to be loaded: two
 * reads of one stored quadrant would produce two different expeditions for the
 * same pilot, and a hull that reconnected would stop being its own ally.
 *
 * ## Why it is shaped like a UUID
 *
 * Because the composition's contract says an expedition id is a UUID, and a
 * derived id that failed that contract would force a second, laxer decoder for
 * old records - which is how a format ends up with two definitions of legal.
 *
 * It is a version 8 UUID: the variant RFC 9562 reserves for exactly this, an
 * identifier whose bits come from an application's own rule rather than from a
 * clock or a random source. Two 64-bit FNV-1a folds over the account, one
 * salted, with the version and variant nibbles pinned - deterministic in Node,
 * in workerd and in a browser, to the bit.
 *
 * **Not a secret and not a capability.** It names a party of one; nothing is
 * authorised by holding it, and a pilot who forged one would be forging a
 * composition the seating boundary never issued.
 */
export function openWorldDerivedExpeditionId(accountId: string): string {
  return derivedUuid(`kenocyber:open-world:solo:${accountId}`);
}

/**
 * **A expedição seguinte de uma conta sozinha** (gate V28I.BY).
 *
 * `openWorldDerivedExpeditionId` dava a mesma expedição a todas as entradas de
 * uma conta, e isso fazia de cada entrada nova a expedição que já tinha acabado:
 * a mesma identidade, a mesma carga selada, e uma extração que nunca mais podia
 * acontecer. **Uma expedição acabada deixa de existir, e a próxima é outra.**
 *
 * Continua derivada e não cunhada, pela razão de sempre: repetir a entrada tem
 * de encontrar a mesma expedição e as mesmas vagas. A semente é a expedição que
 * acabou — única por construção, porque cada uma nasce da anterior — e a
 * primeira de todas continua a ser a derivada da conta, que é o que os registos
 * antigos já escreveram.
 */
export function openWorldNextSoloExpeditionId(
  accountId: string,
  previousExpeditionId: string,
): string {
  return derivedUuid(`kenocyber:open-world:solo:${accountId}:after:${previousExpeditionId}`);
}

/** Um UUID versão 8 a partir de uma semente: duas dobras FNV-1a, uma salgada. */
function derivedUuid(seed: string): string {
  const high = fnv1a64(seed);
  const low = fnv1a64(`${seed}:low`);
  const hex = `${high.toString(16).padStart(16, '0')}${low.toString(16).padStart(16, '0')}`;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    /* Version 8: "this identifier means whatever its application says it does". */
    `8${hex.slice(13, 16)}`,
    /* Variant 10xx, which is the one every RFC 9562 UUID carries. */
    `${'89ab'[Number.parseInt(hex[16] as string, 16) & 0b11] as string}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

/** The 64-bit fold the digest uses, shared so there is one of it. */
function fnv1a64(text: string): bigint {
  const mask = (1n << 64n) - 1n;
  const prime = 0x100000001b3n;
  let hash = 0xcbf29ce484222325n;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash ^ BigInt(text.charCodeAt(index))) * prime) & mask;
  }
  return hash;
}
